import React, { useState } from 'react';
import { X, Plus, FileText, ChevronDown, ChevronUp, Edit2, Trash2, Check, MessageSquare } from 'lucide-react';

const DayDetailModal = ({
  isOpen,
  onClose,
  date,
  trades,
  habitLog,
  tradingPlanRules,
  onAddTrade,
  onEditTrade,
  onDeleteTrade,
  onSaveHabitLog
}) => {
  const [expandedNotes, setExpandedNotes] = useState({});
  const [showHabitSection, setShowHabitSection] = useState(true);
  
  // Check if this day has any saved habit data for CURRENT rules
  // (old entries for deleted rules don't count)
  const hasExistingHabitData = React.useMemo(() => {
    if (!habitLog || !habitLog.entries || !tradingPlanRules) return false;
    // Check if any saved entry matches a current rule
    return habitLog.entries.some(entry => 
      tradingPlanRules.some(rule => rule.id === entry.rule_id)
    );
  }, [habitLog, tradingPlanRules]);
  
  // Compute habit entries by merging current rules with saved log
  // This ensures new rules show up (unchecked) on previously tracked days
  // AND old deleted rules are not counted
  const computedHabitEntries = React.useMemo(() => {
    if (!tradingPlanRules || tradingPlanRules.length === 0) {
      return [];
    }
    
    // Create a map of saved entries for quick lookup
    const savedEntriesMap = {};
    if (habitLog && habitLog.entries) {
      habitLog.entries.forEach(entry => {
        // Only use entries that match CURRENT rules (ignore deleted rules)
        if (tradingPlanRules.some(r => r.id === entry.rule_id)) {
          savedEntriesMap[entry.rule_id] = entry;
        }
      });
    }
    
    // Create entries ONLY for current rules
    return tradingPlanRules.map(rule => {
      if (savedEntriesMap[rule.id]) {
        return savedEntriesMap[rule.id];
      }
      // New rule or rule with no saved data - show as unchecked
      return {
        rule_id: rule.id,
        followed: false,
        notes: ''
      };
    });
  }, [habitLog, tradingPlanRules]);
  
  const computedHabitNotes = React.useMemo(() => {
    const notesMap = {};
    // Get notes from saved habit log entries (only for current rules)
    if (habitLog && habitLog.entries && tradingPlanRules) {
      habitLog.entries.forEach(entry => {
        // Only use notes from entries that match current rules
        if (tradingPlanRules.some(r => r.id === entry.rule_id)) {
          notesMap[entry.rule_id] = entry.notes || '';
        }
      });
    }
    // Ensure all current rules have an entry (even if empty)
    if (tradingPlanRules) {
      tradingPlanRules.forEach(rule => {
        if (!(rule.id in notesMap)) {
          notesMap[rule.id] = '';
        }
      });
    }
    return notesMap;
  }, [habitLog, tradingPlanRules]);
  
  // Local state for editing
  const [habitEntries, setHabitEntries] = useState(computedHabitEntries);
  const [habitNotes, setHabitNotes] = useState(computedHabitNotes);
  
  // Reset state when date, rules, or habitLog changes
  React.useEffect(() => {
    setHabitEntries(computedHabitEntries);
    setHabitNotes(computedHabitNotes);
  }, [date, tradingPlanRules, habitLog, computedHabitEntries, computedHabitNotes]);
  
  if (!isOpen) return null;
  
  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  const toggleNotes = (tradeId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [tradeId]: !prev[tradeId]
    }));
  };
  
  const handleHabitToggle = (ruleId) => {
    setHabitEntries(prev => prev.map(entry => 
      entry.rule_id === ruleId 
        ? { ...entry, followed: !entry.followed }
        : entry
    ));
  };
  
  const handleHabitNoteChange = (ruleId, note) => {
    setHabitNotes(prev => ({ ...prev, [ruleId]: note }));
    setHabitEntries(prev => prev.map(entry =>
      entry.rule_id === ruleId
        ? { ...entry, notes: note }
        : entry
    ));
  };
  
  const handleSaveHabits = () => {
    const entriesWithNotes = habitEntries.map(entry => ({
      ...entry,
      notes: habitNotes[entry.rule_id] || ''
    }));
    onSaveHabitLog(date, entriesWithNotes);
  };
  
  // Calculate habit score
  // Returns null if no current rules exist
  // Returns score based only on CURRENT rules
  const calculateHabitScore = () => {
    if (habitEntries.length === 0) return null;
    const followed = habitEntries.filter(e => e.followed).length;
    const score = (followed / habitEntries.length) * 100;
    return Number(score.toFixed(1));
  };
  
  // Calculate total P/L considering outcomes (wins add, losses subtract)
  const totalPL = trades?.reduce((sum, t) => {
    const pl = Math.abs(parseFloat(t.profitLoss) || 0);
    if (t.outcome?.toLowerCase() === 'loss') {
      return sum - pl;
    }
    return sum + pl;
  }, 0) || 0;
  
  // Calculate total points - only count trades where points were entered
  const pointsData = trades?.reduce((acc, t) => {
    const pipsPoints = t.pipsPoints ?? t.pips_points;
    if (pipsPoints !== null && pipsPoints !== undefined && pipsPoints !== '') {
      acc.hasPoints = true;
      const pointsValue = Math.abs(parseFloat(pipsPoints) || 0);
      if (t.outcome?.toLowerCase() === 'loss') {
        acc.total -= pointsValue;
      } else {
        acc.total += pointsValue;
      }
    }
    return acc;
  }, { total: 0, hasPoints: false }) || { total: 0, hasPoints: false };
  
  const habitScore = calculateHabitScore();
  
  // Determine if we should show the habit score or "Not tracked yet"
  // Show score if: has existing saved data for current rules OR user has checked something
  const anyHabitsChecked = habitEntries.some(e => e.followed);
  const showHabitScore = hasExistingHabitData || anyHabitsChecked;
  
  const getRuleText = (ruleId) => {
    const rule = tradingPlanRules?.find(r => r.id === ruleId);
    return rule?.rule_text || 'Unknown rule';
  };
  
  return (
    <div className="day-modal-overlay" onClick={onClose}>
      <div className="day-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="day-modal-header">
          <h2>{formatDate(date)}</h2>
          <button className="day-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <div className="day-modal-body">
          {/* Trading Plan Checklist Section */}
          {tradingPlanRules && tradingPlanRules.length > 0 && (
            <div className="day-modal-section">
              <div 
                className="section-header clickable"
                onClick={() => setShowHabitSection(!showHabitSection)}
              >
                <div className="section-title">
                  <span className="section-icon">📋</span>
                  <span>Trading Plan Checklist</span>
                  {showHabitScore && habitScore !== null && (
                    <span className={`habit-score-badge ${habitScore >= 80 ? 'good' : habitScore >= 50 ? 'medium' : 'poor'}`}>
                      {habitScore}%
                    </span>
                  )}
                </div>
                {showHabitSection ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </div>
              
              {showHabitSection && (
                <div className="habit-checklist">
                  {habitEntries.map((entry, index) => {
                    const rule = tradingPlanRules.find(r => r.id === entry.rule_id);
                    if (!rule) return null;
                    
                    return (
                      <div key={entry.rule_id} className="habit-item">
                        <div className="habit-row">
                          <label className="habit-checkbox-label">
                            <input
                              type="checkbox"
                              checked={entry.followed}
                              onChange={() => handleHabitToggle(entry.rule_id)}
                              className="habit-checkbox"
                            />
                            <span className={`habit-checkmark ${entry.followed ? 'checked' : ''}`}>
                              {entry.followed && <Check size={14} />}
                            </span>
                            <span className={`habit-text ${entry.followed ? 'followed' : ''}`}>
                              {rule.rule_text}
                            </span>
                          </label>
                          <button
                            className="habit-note-toggle"
                            onClick={() => setHabitNotes(prev => ({ 
                              ...prev, 
                              [`show_${entry.rule_id}`]: !prev[`show_${entry.rule_id}`] 
                            }))}
                            title="Add note"
                          >
                            <MessageSquare size={14} />
                          </button>
                        </div>
                        
                        {(habitNotes[`show_${entry.rule_id}`] || entry.notes) && (
                          <div className="habit-note-input">
                            <textarea
                              placeholder="Add a note about this rule..."
                              value={habitNotes[entry.rule_id] || ''}
                              onChange={(e) => handleHabitNoteChange(entry.rule_id, e.target.value)}
                              rows={2}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  <div className="habit-actions">
                    <div className="habit-score-display">
                      Today&apos;s Score: <strong>{showHabitScore ? `${habitScore}%` : 'Not tracked yet'}</strong>
                      {showHabitScore && (
                        <span className="habit-fraction">
                          ({habitEntries.filter(e => e.followed).length}/{habitEntries.length} rules followed)
                        </span>
                      )}
                    </div>
                    <button className="save-habits-btn" onClick={handleSaveHabits}>
                      Save Checklist
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Trades Section */}
          <div className="day-modal-section">
            <div className="section-header">
              <div className="section-title">
                <span className="section-icon">📊</span>
                <span>Trades</span>
                <span className={`trades-summary ${totalPL >= 0 ? 'positive' : 'negative'}`}>
                  ({trades?.length || 0} trades, {totalPL >= 0 ? '+' : ''}{totalPL.toFixed(2)}{pointsData.hasPoints && (<>, <span className={pointsData.total >= 0 ? 'positive' : 'negative'}>{pointsData.total >= 0 ? '+' : ''}{pointsData.total % 1 === 0 ? pointsData.total : pointsData.total.toFixed(2)}</span></>)})
                </span>
              </div>
              <button className="add-trade-btn-small" onClick={() => onAddTrade(date)}>
                <Plus size={16} />
                Add Trade
              </button>
            </div>
            
            {trades && trades.length > 0 ? (
              <div className="day-trades-list">
                {trades.map(trade => (
                  <div key={trade.id} className="day-trade-item">
                    <div className="trade-main-row">
                      <div className="trade-pair-direction">
                        <span className="trade-pair">{trade.pair}</span>
                        <span className={`trade-direction ${trade.direction?.toLowerCase()}`}>
                          {trade.direction}
                        </span>
                      </div>
                      
                      <div className="trade-time-session">
                        {trade.time && <span className="trade-time">{trade.time}</span>}
                        <span className="trade-session">{trade.session}</span>
                      </div>
                      
                      <div className={`trade-pl ${trade.outcome?.toLowerCase() === 'win' ? 'positive' : trade.outcome?.toLowerCase() === 'loss' ? 'negative' : ''}`}>
                        ${Math.abs(parseFloat(trade.profitLoss) || 0).toFixed(2)}
                      </div>
                      
                      <div className={`trade-outcome ${trade.outcome?.toLowerCase()}`}>
                        {trade.outcome}
                      </div>
                      
                      <div className="trade-actions-mini">
                        {trade.notes && (
                          <button 
                            className="trade-action-btn notes"
                            onClick={() => toggleNotes(trade.id)}
                            title="View notes"
                          >
                            <FileText size={14} />
                          </button>
                        )}
                        <button 
                          className="trade-action-btn edit"
                          onClick={() => onEditTrade(trade)}
                          title="Edit trade"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button 
                          className="trade-action-btn delete"
                          onClick={() => onDeleteTrade(trade.id)}
                          title="Delete trade"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    
                    {expandedNotes[trade.id] && trade.notes && (
                      <div className="trade-notes-expanded">
                        <FileText size={14} className="notes-icon" />
                        <span>{trade.notes}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-trades-message">
                No trades logged for this day. Click &quot;Add Trade&quot; to log your first trade.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayDetailModal;
